import { ICompetition } from 'src/interfaces/Competition';
import { IEdition } from 'src/interfaces/Edition';
import { TechnicalType } from 'src/interfaces/TechnicalType';
import { ITrackMap } from 'src/interfaces/TrackMap';
import { isPublished } from 'src/helpers/publishedFilter';

/** Normalize ids from API (string, ObjectId, or populated { id } / { _id }). */
export function resolveResourceId(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const record = value as { id?: unknown; _id?: unknown };
    if (record.id != null) return String(record.id);
    if (record._id != null) return String(record._id);
  }
  return String(value);
}

function resolveTrackMapDistanceId(track: Pick<ITrackMap, 'distance'>): string {
  return resolveResourceId(track.distance);
}

function editionEntryIncludesDistance(distances: string[] | undefined, distanceId: string): boolean {
  if (!distances?.length) return false;
  const target = resolveResourceId(distanceId);
  return distances.some(d => resolveResourceId(d) === target);
}

function collectDistanceIdsToResolve(
  distanceId: string,
  distancesById?: Record<string, TechnicalType>,
): string[] {
  const resolved = new Set<string>();
  const primary = resolveResourceId(distanceId);
  if (primary) resolved.add(primary);

  const distance = distancesById?.[primary];
  if (!distance?.name) return [...resolved];

  Object.values(distancesById ?? {}).forEach(entry => {
    if (entry.name === distance.name) {
      resolved.add(resolveResourceId(entry.id));
    }
  });

  return [...resolved];
}

/** Competition ids linked to a distance across all editions (union). */
export function collectCompetitionIdsForDistance(
  distanceId: string,
  editions: IEdition[],
  options?: {
    preferredEditionId?: string;
    distancesById?: Record<string, TechnicalType>;
  },
): string[] {
  const ids = new Set<string>();
  const distanceIds = collectDistanceIdsToResolve(distanceId, options?.distancesById);

  const addFromEdition = (edition: IEdition | undefined) => {
    edition?.competitions?.forEach(entry => {
      const competitionId = resolveResourceId(entry.competitionId);
      if (!competitionId) return;
      if (distanceIds.some(id => editionEntryIncludesDistance(entry.distances, id))) {
        ids.add(competitionId);
      }
    });
  };

  if (options?.preferredEditionId) {
    addFromEdition(
      editions.find(e => resolveResourceId(e.id) === resolveResourceId(options.preferredEditionId)),
    );
  }

  editions.forEach(addFromEdition);

  return [...ids];
}

function competitionIdsToIcons(
  competitionIds: string[],
  competitionsById: Record<string, ICompetition>,
): string[] {
  const icons: string[] = [];
  for (const competitionId of competitionIds) {
    const competition = competitionsById[competitionId];
    if (!competition || !isPublished(competition) || !competition.icon) continue;
    if (!icons.includes(competition.icon)) {
      icons.push(competition.icon);
    }
  }
  return icons;
}

export function getCompetitionIconsForDistance(
  distanceId: string,
  options: {
    editions?: IEdition[];
    editionsById?: Record<string, IEdition>;
    activeEditionId?: string;
    competitionsById?: Record<string, ICompetition>;
    distancesById?: Record<string, TechnicalType>;
    peerTracks?: Pick<ITrackMap, 'distance'>[];
  },
): string[] {
  const { editions, editionsById, activeEditionId, competitionsById, distancesById, peerTracks } =
    options;
  if (!competitionsById) return [];

  const editionList = editions ?? (editionsById ? Object.values(editionsById) : []);
  if (!editionList.length) return [];

  const competitionIds = collectCompetitionIdsForDistance(distanceId, editionList, {
    preferredEditionId: activeEditionId,
    distancesById,
  });

  let icons = competitionIdsToIcons(competitionIds, competitionsById);

  if (!icons.length && peerTracks?.length) {
    const peerIconCounts = new Map<string, number>();
    for (const track of peerTracks) {
      const peerDistanceId = resolveTrackMapDistanceId(track);
      if (peerDistanceId === resolveResourceId(distanceId)) continue;
      const peerIcons = competitionIdsToIcons(
        collectCompetitionIdsForDistance(peerDistanceId, editionList, {
          preferredEditionId: activeEditionId,
          distancesById,
        }),
        competitionsById,
      );
      peerIcons.forEach(icon => peerIconCounts.set(icon, (peerIconCounts.get(icon) ?? 0) + 1));
    }
    const sorted = [...peerIconCounts.entries()].sort((a, b) => b[1] - a[1]);
    if (sorted.length === 1 || (sorted[0] && sorted[0][1] >= 2)) {
      icons = sorted[0] ? [sorted[0][0]] : [];
    }
  }

  return icons;
}

export function getCompetitionIconsForTrack(
  track: Pick<ITrackMap, 'distance'>,
  peerTracks: Pick<ITrackMap, 'distance'>[] | undefined,
  options: Omit<Parameters<typeof getCompetitionIconsForDistance>[1], 'peerTracks'>,
): string[] {
  return getCompetitionIconsForDistance(resolveTrackMapDistanceId(track), {
    ...options,
    peerTracks,
  });
}
